import React, { useState, useEffect, useCallback } from 'react';
import { assetLogsApi } from '../../api';
import { AssetLog, AssetLogAction } from '../../types';
import { parseAsUTC } from '../../utils/helpers';
import { Card } from '../../components/common/Card';
import { Icons } from '../../components/common/Icons';
import './AssetLogs.css';

export function AssetLogs() {
  const [logs, setLogs] = useState<AssetLog[]>([]);
  const [actions, setActions] = useState<AssetLogAction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const [selectedAction, setSelectedAction] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await assetLogsApi.getLogs({
        action: selectedAction || undefined,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
        page,
        per_page: 50,
      });
      setLogs(res.logs);
      setTotalPages(res.pages);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load logs');
    } finally {
      setLoading(false);
    }
  }, [selectedAction, dateFrom, dateTo, page]);

  const fetchActions = async () => {
    try {
      const res = await assetLogsApi.getActionTypes();
      setActions(res.actions);
    } catch {
      // Ignore
    }
  };

  useEffect(() => {
    fetchActions();
  }, []);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return parseAsUTC(dateStr).toLocaleString('en-US', { timeZone: 'Asia/Kolkata' });
  };

  const getActionIcon = (action: string) => {
    switch (action) {
      case 'created': return <Icons.Plus className="action-icon created" />;
      case 'updated': return <Icons.Edit className="action-icon updated" />;
      case 'deleted': return <Icons.Trash className="action-icon deleted" />;
      case 'assigned': return <Icons.UserPlus className="action-icon assigned" />;
      case 'returned': return <Icons.UserMinus className="action-icon returned" />;
      case 'state_changed': return <Icons.Refresh className="action-icon state-changed" />;
      default: return <Icons.Activity className="action-icon" />;
    }
  };

  const formatDetails = (details: Record<string, any> | null) => {
    if (!details) return '-';
    try {
      // Show a summary of changes
      const keys = Object.keys(details);
      if (keys.length === 0) return '-';
      if (keys.length <= 3) {
        return keys.map(k => `${k}: ${JSON.stringify(details[k])}`).join(', ');
      }
      return `${keys.length} fields changed`;
    } catch {
      return '-';
    }
  };

  return (
    <div className="asset-logs-page">
      <div className="page-header">
        <div className="page-title">
          <h1>Asset Logs</h1>
          <p>View audit trail of all asset actions</p>
        </div>
      </div>

      {error && (
        <div className="error-banner">
          <Icons.AlertCircle />
          <span>{error}</span>
          <button onClick={() => setError(null)}>&times;</button>
        </div>
      )}

      <Card>
        <div className="logs-toolbar">
          <select
            value={selectedAction}
            onChange={(e) => { setSelectedAction(e.target.value); setPage(1); }}
            className="filter-select"
          >
            <option value="">All Actions</option>
            {actions.map(action => (
              <option key={action.value} value={action.value}>{action.label}</option>
            ))}
          </select>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
            className="date-input"
            placeholder="From"
          />
          <input
            type="date"
            value={dateTo}
            onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
            className="date-input"
            placeholder="To"
          />
        </div>

        {loading ? (
          <div className="loading-state">Loading logs...</div>
        ) : logs.length === 0 ? (
          <div className="empty-state">
            <Icons.FileText />
            <h3>No logs found</h3>
            <p>Asset activity will appear here</p>
          </div>
        ) : (
          <>
            <div className="logs-list">
              {logs.map(log => (
                <div key={log.id} className="log-item">
                  <div className="log-icon">
                    {getActionIcon(log.action)}
                  </div>
                  <div className="log-content">
                    <div className="log-header">
                      <span className="log-action">{log.action.replace('_', ' ').toUpperCase()}</span>
                      <span className="log-asset">{log.asset_code || 'Unknown Asset'}</span>
                    </div>
                    <div className="log-details">{formatDetails(log.details)}</div>
                    <div className="log-meta">
                      <span>By: {log.performer_name || 'Unknown'}</span>
                      <span>{formatDate(log.performed_at)}</span>
                      {log.ip_address && <span>IP: {log.ip_address}</span>}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {totalPages > 1 && (
              <div className="pagination">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  Previous
                </button>
                <span>Page {page} of {totalPages}</span>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
}

export default AssetLogs;
